import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setPersonAssociationsBulk, type BulkPersonAssociationResult, type ManagedAssociationKind } from "../../api/entitlementAdmin";
import { parseBulkEmails } from "../../routes/admin/bulkEmails";
import { useToast } from "../toast/toastContext";
import styles from "./Admin.module.css";

/**
 * Assigns a list of email addresses to a Department in one go. It goes in a dialog opened from a unit's Members row. The addresses are
 * parsed with the same rules as the other bulk forms, and the outcome comes back as a toast.
 */
export function BulkAssociationSection({
  kind,
  value,
  onComplete,
}: {
  kind: ManagedAssociationKind;
  value: string;
  onComplete: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [emails, setEmails] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ error?: string; success?: BulkPersonAssociationResult } | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const uniqueEmails = parseBulkEmails(emails);
    if (uniqueEmails.length === 0) {
      toast.show(t("admin.entitlement.bulk.validation"), "error");
      return;
    }

    setIsSubmitting(true);
    const result = await setPersonAssociationsBulk(uniqueEmails, kind, value);
    setIsSubmitting(false);
    if (result.kind === "ok") {
      toast.show(t("admin.entitlement.bulk.success", { count: result.response.updatedCount }), "success");
      setFeedback({ success: result.response });
      await onComplete();
    } else if (result.kind === "denied") {
      toast.show(t("admin.entitlement.denied.description"), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <form className={styles.form} onSubmit={(event) => void submit(event)}>
      <p className={styles.assignUsersInstructions}>{t("admin.entitlement.bulk.description")}</p>
      <div className={`${styles.field} ${styles.bulkEmailsField}`}>
        <textarea className={styles.bulkEmailsTextarea} id="bulk-association-emails" aria-label={t("admin.entitlement.bulk.emails")} rows={16} value={emails} onChange={(event) => setEmails(event.target.value)} required />
      </div>
      {feedback?.success && (
        <>
          {feedback.success.notFoundEmails.length > 0 && <p>{t("admin.entitlement.bulk.notFound", { emails: feedback.success.notFoundEmails.join(", ") })}</p>}
        </>
      )}
      <button type="submit" className="button button-primary" disabled={isSubmitting}>
        {isSubmitting ? t("admin.entitlement.bulk.submitting") : t("admin.entitlement.bulk.submit")}
      </button>
    </form>
  );
}
